import chapterImg5 from '../../../../assets/images/courses/collaboration-fundamentals/chapter7.1.jpg';
import introImg from '../../../../assets/images/courses/collaboration-fundamentals/chapter7.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { MediaBlock } from '../../MediaBlock';

interface CollaborationFundamentalsProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const CollaborationFundamentalsChapter7: React.FC<CollaborationFundamentalsProps> = ({
  chapter,
  onNextChapter,
  totalPages,
  // courseId,
}) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl ">Summary</h2>

        <MediaBlock mediaImage={introImg}>
          <p className="text-2xl text-left font-light mt-10 mb-5">
            Remember: Employers value employees who collaborate well in teams. There is a very good
            chance you will frequently be part of a team when you are working.
          </p>
        </MediaBlock>
        <p className="text-2xl text-left font-bold mt-10 mb-5">
          In conclusion, the "Collaboration Fundamentals" module has equipped you with essential
          knowledge and skills to excel in collaborative environments. You now understand the
          importance of effective teamwork and have explored various roles and responsibilities that
          contribute to a successful team.
        </p>
        <div className="md:flex items-center mb-7">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg5} alt="" />
          </div>
          <p className="flex-1 ">
            By recognizing different engagement styles and applying practical strategies, you can
            enhance your collaborative efforts and achieve common goals more efficiently. Remember,
            collaboration is not just about working together; it's about fostering a supportive,
            creative, and productive environment where everyone can thrive. Embrace these
            principles, and you will be well-prepared to tackle challenges and seize opportunities
            in both your personal and professional life.
          </p>
          <br />
          <br />
        </div>

        <h3 className="text-2xl  font-bold mt-4 mb-1">
          Throughout this lesson, you have learned the following:
        </h3>
        <ol className="list-disc pl-5 mt-14 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">
            What collaboration is and why it's important in different settings like school, home,
            and work.
          </li>
          <li className="pl-9 mb-7">
            Different ways to collaborate and the importance of effective teamwork.
          </li>
          <li className="pl-9 mb-7">
            Real-life examples of collaboration, such as in innovative projects and professional
            settings.
          </li>
          <li className="pl-9 mb-7">
            Your own engagement style and how to work effectively with others.
          </li>
          <li className="pl-9 mb-7">
            The essential role of teamwork in achieving common goals and supporting each other.
          </li>
        </ol>

        <h3 className="text-2xl text-purple-600 font-bold mt-4 mb-1">Coming Up Next</h3>
        <p className="text-2xl text-left font-bold mt-10 mb-5">
          The next module explores the various roles within a team, the stages of task completion,
          and the characteristics that make a great team player. By understanding and embracing
          diverse team roles, participants will learn to contribute meaningfully and efficiently to
          their teams, enhancing overall performance and satisfaction.
        </p>

        <div className="mt-16">
          <h3 className="text-2xl text-purple-600 font-bold mt-4 mb-1">References</h3>

          <p className="text-xl text-left font-light mt-10 mb-5">
            Baker, W. E. and Bulkley, N. 2014. Paying it Forward versus Rewarding Reputation:
            Mechanisms of Generalized Reciprocity.” Organization Science (25), 1493-1510.
          </p>

          <p className="text-xl text-left font-light mt-10 mb-5">
            Belbin, R. M. (1981). Management Teams: Why They Succeed or Fail. Oxford:
            Butterworth-Heinemann.
          </p>

          <p className="text-xl text-left font-light mt-10 mb-5">
            Belbin, R. M. (2010). Team Roles at Work (2nd ed.). Oxford: Butterworth-Heinemann.
          </p>

          <p className="text-xl text-left font-light mt-10 mb-5">
            <a
              target="_blank"
              href="https://www.betterup.com/blog/benefits-of-feedback"
              className="underline text-purple-600"
            >
              BetterUp - Why is Feedback Important
            </a>
          </p>

          <p className="text-xl text-left font-light mt-10 mb-5">
            Buckingham, M., & Clifton, D. O. (2001). Now, Discover Your Strengths. New York: Free
            Press.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Edmondson, Amy C.{' '}
            <a
              target="_blank"
              href="https://www.wiley.com/en-us/The+Fearless+Organization%3A+Creating+Psychological+Safety+in+the+Workplace+for+Learning%2C+Innovation%2C+and+Growth-p-9781119477266"
              className="underline text-purple-600"
            >
              The Fearless Organization: Creating Psychological Safety in the Workplace for
              Learning, Innovation, and Growth.
            </a>{' '}
            Hoboken, NJ: John Wiley & Sons, 2018.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Edmondson, A. C. (2018).{' '}
            <a
              target="_blank"
              href="https://hbr.org/2011/09/the-power-of-teamwork"
              className="underline text-purple-600"
            >
              "The Power of Teamwork: Psychological Safety, Team Learning, and Organizational
              Performance"
            </a>
            . Harvard Business Review.{' '}
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Ely, R. J., & Thomas, D. A. (2001). "Cultural Diversity at Work: The Effects of
            Diversity Perspectives on Work Group Processes and Outcomes". Administrative Science
            Quarterly, 46(2), 229-273.
          </p>

          <p className="text-xl text-left font-light mt-10 mb-5">
            <a
              target="_blank"
              href="https://www.forbes.com/sites/forbestechcouncil/2022/02/09/the-importance-and-mechanics-of-building-a-culture-of-feedback/?sh=4945a6e16810"
              className="underline text-purple-600"
            >
              Forbes - The Importance of Feedback in the Workplace
            </a>
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            <a
              target="_blank"
              href="https://www.gallup.com/workplace/271184/effective-feedback-kind-ask.aspx"
              className="underline text-purple-600"
            >
              Gallup - The Power of Positive Feedback
            </a>
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Hackman, J. R. (2002). Leading Teams: Setting the Stage for Great Performances. Boston:
            Harvard Business Review Press.{' '}
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            <a
              target="_blank"
              href="https://www.mindtools.com/a18a0wu/giving-feedback"
              className="underline text-purple-600"
            >
              Harvard Business Review - The Feedback Fallacy{' '}
            </a>
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Herrmann, N. (1996). The Whole Brain Business Book: Unlocking the Power of Whole Brain
            Thinking in Organizations and Individuals. McGraw-Hill Education.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Herrmann, N., & Herrmann-Nehdi, A. (2015). The Whole Brain Business Book: Second
            Edition. McGraw-Hill Education.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            <a
              target="_blank"
              href="https://ca.indeed.com/career-advice/career-development/how-to-give-constructive-feedback"
              className="underline text-purple-600"
            >
              Indeed - How to Give Constructive Feedback
            </a>
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Insights Discovery Website:{' '}
            <a
              target="_blank"
              href="https://www.insights.com/products/insights-discovery"
              className="underline text-purple-600"
            >
              https://www.insights.com/products/insights-discovery{' '}
            </a>
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Katz, D., & Kahn, R. L. (20088). The Social Psychology of Organizations (4th ed.). New
            York: Wiley.{' '}
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Koft, Chris. (2020). Teamwork Foundations: The five stages of a team task.{' '}
            <a
              target="_blank"
              href="https://www.linkedin.com/learning/teamwork-foundations-2020/the-five-stages-of-a-team-task"
              className="underline text-purple-600"
            >
              LinkedIn
            </a>
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Kozlowski, S. W. J., & Bell, B. S. (2018). "Work Groups and Teams in Organizations:
            Review Update". Annual Review of Organizational Psychology and Organizational Behavior,
            5, 213-236.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Lencioni, P. (2002). The Five Dysfunctions of a Team: A Leadership Fable. San Francisco:
            Jossey-Bass.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Levi, D. (2020). Group Dynamics for Teams (6th ed.). Los Angeles, CA: Sage Publications.{' '}
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            <a
              target="_blank"
              href="https://www.mindtools.com/a18a0wu/giving-feedback"
              className="underline text-purple-600"
            >
              Mind Tools - Giving and Receiving Feedback{' '}
            </a>
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Page, S. E. (2007). The Difference: How the Power of Diversity Creates Better Groups,
            Firms, Schools, and Societies. Princeton, NJ: Princeton University Press.{' '}
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Rath, T. & Conchie, B. (2018). Strengths Based Leadership: Great Leaders, Teams, and Why
            People Follow. New York: Gallup Press.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Rock, D., Grant, H., & Grey, J. (2016).{' '}
            <a
              target="_blank"
              href="https://hbr.org/2016/09/diverse-teams-feel-less-comfortable-and-thats-why-they-perform-better"
              className="underline text-purple-600"
            >
              "Diverse Teams Feel Less Comfortable — and That’s Why They Perform Better"
            </a>
            . Harvard Business Review.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Ross, Rick. (1994). The Fifth Discipline Fieldbook. The Fifth Discipline Fieldbook (New
            York: Doubleday, 1994) is available through Pegasus Communications, Inc.
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            Stewart, G. L., Manz, C. C., & Sims, H. P. (1999). Team Work and Group Dynamics. New
            York: Wiley.{' '}
          </p>
          <p className="text-xl text-left font-light mt-10 mb-5">
            <a
              target="_blank"
              href="https://www.linkedin.com/pulse/unlock-power-love-framework-hybrid-performance-group/"
              className="underline text-purple-600"
            >
              Unlock the Power of the LOVE Framework!
            </a>
            Hybrid Performance Group, June 26, 2023
          </p>
        </div>

        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Quiz
        </button>
      </div>
    </>
  );
};
